<template>
  <Collection :collection-id="selectedCollectionId">
    <template #collection-header="{ hasDatasets: hasStagedData }">
      <div class="collection-form">
        <fieldset :disabled="hasStagedData">
          <div class="field is-horizontal">
            <div class="field-label">
              <label class="label">Name</label>
            </div>
            <div class="field-body is-flex is-flex-direction-column">
              <div class="field is-expanded">
                <p class="control is-expanded">
                  <input
                    ref="inputName"
                    class="input is-small"
                    :class="errors['no-name'] ? 'is-danger' : ''"
                    :value="collectionName"
                    :disabled="hasStagedData"
                    @input="(e) => (collectionName = e.target.value)"
                    @change="(e) => (collectionName = e.target.value.trim())"
                  />
                </p>
              </div>
              <p v-if="errors['no-name']" class="has-text-danger is-size-7 p-1">
                {{ errors['no-name'] }}
              </p>
            </div>
          </div>
          <div class="field is-horizontal">
            <div class="field-label">
              <label class="label">Number of datafile(s) per dataset</label>
            </div>
            <div class="field-body">
              <div class="field is-expanded">
                <div class="buttons has-addons" :disabled="hasStagedData">
                  <button
                    class="button is-small is-outlined mb-0"
                    :disabled="dimensionsCount <= MIN_DIMENSIONS"
                    @click="dimensionsCount--"
                  >
                    <span class="icon">
                      <i class="fa fa-minus" />
                    </span>
                  </button>
                  <input
                    v-model.number="dimensionsCount"
                    type="text"
                    class="input is-small"
                    :class="{
                      'is-danger':
                        dimensionsCount < MIN_DIMENSIONS ||
                        dimensionsCount > MAX_DIMENSIONS,
                    }"
                    :min="MIN_DIMENSIONS"
                    :max="MAX_DIMENSIONS"
                  />
                  <button
                    class="button is-small is-outlined mb-0"
                    :disabled="dimensionsCount >= MAX_DIMENSIONS"
                    @click="dimensionsCount++"
                  >
                    <span class="icon">
                      <i class="fa fa-plus" />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="field is-horizontal">
            <div class="field-label">
              <label class="label">
                Raw data
                <span
                  v-tooltip="{
                    content: `Raw data ${
                      (assayId && 'is mandatory on assays') ||
                      'cannot be loaded without an assay'
                    }`,
                  }"
                  class="has-text-primary hoverable-icon icon pl-2"
                >
                  <i class="fa-solid fa-sm fa-question-circle" />
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <button
                  :disabled="!canChangeRawData || hasStagedData"
                  class="control button is-borderless"
                  @click="setIsRawDataCollection(!isRawDataCollection)"
                >
                  <span
                    class="icon"
                    :class="{
                      'has-text-primary': isRawDataCollection,
                    }"
                  >
                    <i
                      class="fa"
                      :class="{
                        'fa-toggle-on': isRawDataCollection,
                        'fa-toggle-off has-text-grey': !isRawDataCollection,
                      }"
                    />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </fieldset>
        <template v-if="remainingFilesCount > 0">
          <div
            class="collection-icon-handles"
            style="position: absolute; top: 0; right: 0"
          >
            <span class="icon hoverable has-text-primary" @click="toggleHelp">
              <i class="fa fa-info-circle" />
            </span>
            <div v-tooltip="'Show advanced controls'" class="has-exponent-tag">
              <span
                class="icon hoverable has-text-primary"
                @click="showAdvancedControls = true"
              >
                <i class="fa fa-cog" />
                <span
                  v-if="advancedFiltersNumber > 0"
                  class="tag exponent-tag is-rounded is-success has-text-weight-bold"
                  style="font-size: 0.7rem; border: none"
                >
                  {{ advancedFiltersNumber }}
                </span>
              </span>
            </div>
          </div>
          <div v-show="showAdvancedControls" class="advanced-controls card">
            <div class="card-content p-3 px-4">
              <button
                class="hoverable icon delete"
                @click="showAdvancedControls = false"
              ></button>
              <div class="title is-size-6 mb-2">
                Advanced collection controls
              </div>
              <ul class="content">
                <li class="name-mode" style="flex-shrink: 1; flex-basis: 0">
                  <div class="label pr-5">
                    <span class="is-size-7 mr-2">Processed file property</span>
                    <span
                      v-tooltip="'Mask part of file names in table columns'"
                      class="has-text-primary hoverable-icon icon is-small"
                    >
                      <i class="fa-solid fa-sm fa-question-circle" />
                    </span>
                  </div>
                  <div class="buttons has-addons is-flex-wrap-nowrap">
                    <button
                      v-for="opt in [
                        { label: 'filename', isUriMode: false },
                        { label: 'URI', isUriMode: true },
                      ]"
                      :key="`opt-name-mode-${opt.label}`"
                      class="button is-small mb-0"
                      :class="{
                        'is-primary is-selected': isUriMode === opt.isUriMode,
                        'is-capitalized': !opt.isUriMode,
                      }"
                      @click="setIsUriMode(opt.isUriMode)"
                    >
                      {{ opt.label }}
                    </button>
                  </div>
                </li>
                <li class="file-filter">
                  <div class="label pr-5">
                    <span class="is-size-7 mr-2">File list filter</span>
                    <span
                      v-tooltip="'Show only files passing this filter'"
                      class="has-text-primary hoverable-icon icon is-small"
                    >
                      <i class="fa-solid fa-sm fa-question-circle" />
                    </span>
                  </div>
                  <InputTextFilter
                    :key="`collectfilter-${selectedCollectionId}`"
                    name="collectionfilter"
                    :init-input="fileFilterInput"
                    :init-is-regex="fileFilterIsRegex"
                    :list-of-values="preCollectionFilterFiles"
                    @input="(s) => (fileFilterInput = s)"
                    @change-is-regex="(r) => (fileFilterIsRegex = r)"
                    @filter-list="(f) => (postCollectionFilterFiles = f)"
                  ></InputTextFilter>
                </li>
                <li class="file-filter">
                  <div class="label pr-5">
                    <span class="is-size-7 mr-2">Dataset name extractor</span>
                    <span
                      v-tooltip="
                        'Use a regular expression to extract dataset names. ' +
                        'Extracted parts with parentheses ( e.g. using (.*) )' +
                        'will be concatenated'
                      "
                      class="has-text-primary hoverable-icon icon is-small"
                    >
                      <i class="fa-solid fa-sm fa-question-circle" />
                    </span>
                  </div>
                  <InputTextFilter
                    :key="`collectnamefilt-${selectedCollectionId}`"
                    name="collectionnamefilt"
                    :can-change-mode="false"
                    :show-filtered-count="false"
                    :init-input="initDatasetNameFiltInput"
                    :init-is-regex="true"
                    :list-of-values="datasetsNames"
                    @input="(s) => (datasetNameFiltInput = s)"
                    @filter-matches="(m) => (datasetsNamesMatches = m)"
                  ></InputTextFilter>
                </li>
                <li class="file-filter">
                  <div class="label pr-5">
                    <span class="is-size-7 mr-2">
                      File {{ isUriMode ? 'path' : 'name' }} mask
                    </span>
                    <span
                      v-tooltip="'Mask a character chain in table columns'"
                      class="has-text-primary hoverable-icon icon is-small"
                    >
                      <i class="fa-solid fa-sm fa-question-circle" />
                    </span>
                  </div>
                  <input
                    v-model="nameMask"
                    class="input is-small"
                    @focus="$event.target.select()"
                  />
                </li>
              </ul>
            </div>
          </div>
        </template>
      </div>
      <div
        v-if="showHelp && remainingFilesCount > 0"
        class="notification is-info is-fullwidth pb-2 m-0 mb-2"
      >
        <button class="delete" @click="toggleHelp" />
        <span class="icon is-medium has-text-primary">
          <i class="fa fa-info-circle fa-lg" />
        </span>
        <span class="text is-size-7 py-4">
          <ul style="list-style: disc">
            <li class="pb-1">
              All files are listed in
              <b>alphabetical order</b>
              in all columns of the table below.
            </li>
            <li class="pb-1">
              When creating a dataset with multiple datafiles, use column
              filters to restrict a specific set of file to a given column. This
              process is easier when the orignal file names are meaningful
            </li>
            <li class="pb-1">
              Use
              <a class="has-text-primary" @click="showAdvancedControls = true">
                advanced collection controls
              </a>
              to (1) filter files in a given collection, (2) improve dataset
              name creation (3) identify files with their URI instead of their
              name, (4) mask certain string in file names
            </li>
          </ul>
        </span>
      </div>
    </template>
    <template
      #collection-details="{ hasDatasets: hasStagedData, datasetsCount }"
    >
      <div class="is-flex is-justify-content-flex-end">
        <span class="tags mb-1">
          <span
            v-if="hasStagedData"
            class="tag is-success is-light has-text-weight-bold"
          >
            <span class="icon pr-2">
              <i class="fa-regular fa-copy fa-sm" />
            </span>
            <span>
              {{ datasetsCount }} staged
              {{ pluralize('dataset', datasetsCount) }}
            </span>
          </span>
        </span>
      </div>
      <DatasetBuilder
        :id="selectedCollectionId"
        :key="`dataset-builder-${selectedCollectionId}`"
        :name="collectionName"
        :files="postCollectionFilterFiles"
        :remaining-files-count="remainingFilesCount"
        :name-mask="nameMask"
        :dimensions-id="dimensionsId"
        :dimensions-values="dimensionsValues"
        :datasets-names="processedDatasetsNames"
        :datasets-properties="datasetsProperties"
        :can-change-dimensions="!hasStagedData"
        :can-stage="canStageData"
        :is-uri-mode="isUriMode"
        :show-help="!hasStagedData"
        @candidate-names="
          (names) => (datasetsNames = names.map((n) => ({ value: n })))
        "
        @set-dimension-value="(dimArgs) => (dimensionsValues = dimArgs)"
      ></DatasetBuilder>
    </template>
  </Collection>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import pluralize from 'pluralize'
import { range } from 'lodash-es'
import { alphaNumSort } from '@/utils'
import Collection from '@/components/data-loader/Collection.vue'

import DatasetBuilder from '@/components/data-loader/DatasetBuilder.vue'
import InputTextFilter from '@/components/InputTextFilter.vue'
import JsonAssayTechDatasetsProps from '@/assets/register-data/assay-tech-datasets-props.json'

const MIN_DIMENSIONS = 1
const MAX_DIMENSIONS = 10

const basename = function (str, sep = '/') {
  return str.substr(str.lastIndexOf(sep) + 1)
}

export default {
  name: 'CollectionBuilder',
  components: {
    Collection,
    DatasetBuilder,
    InputTextFilter,
  },
  props: {
    selectedCollectionId: {
      type: String,
      required: true,
    },
    canChangeRawData: {
      type: Boolean,
      default: false,
    },
    assayId: {
      type: [String, Number],
      default: null,
    },
    assayType: {
      type: String,
      default: null,
    },
    assayTechnology: {
      type: String,
      default: null,
    },
    remainingFilesList: {
      type: Array,
      required: true,
    },
    remainingFilesCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      MIN_DIMENSIONS,
      MAX_DIMENSIONS,
      postCollectionFilterFiles: [],
      showAdvancedControls: false,
      nameMask: '',
      datasetNameFiltInput: '',
      initDatasetNameFiltInput: '',
      datasetsNames: [],
      datasetsNamesMatches: [],
      datasetPropertiesPerAssayTypeJson: JsonAssayTechDatasetsProps,
    }
  },
  computed: {
    ...mapState('dataloader/main', {
      storeShowHelp: (state) => state.showHelp['bldr-collections-table'],
    }),
    ...mapState('dataloader/collections', ['collections']),
    ...mapGetters('dataloader/dropbox', ['dropboxRoot']),
    collection() {
      return this.collections[this.selectedCollectionId]
    },
    collectionName: {
      get() {
        return this.collection?.name
      },
      set(input) {
        this.edit({
          id: this.selectedCollectionId,
          editData: { name: input },
        })
      },
    },
    isUriMode() {
      return this.collection?.isUriMode === true
    },
    isRawDataCollection() {
      return this.collection?.isRawData === true
    },
    fileFilter() {
      return this.collection?.fileFilter
    },
    fileFilterInput: {
      get() {
        return this.fileFilter?.input || ''
      },
      set(input) {
        this.changeFileFilterInput({
          collectionId: this.selectedCollectionId,
          input,
        })
      },
    },
    fileFilterIsRegex: {
      get() {
        return this.fileFilter?.isRegex || false
      },
      set(isRegex) {
        this.changeFileFilterIsRegex({
          collectionId: this.selectedCollectionId,
          isRegex,
        })
      },
    },
    preCollectionFilterFiles() {
      const collectionFiles = this.remainingFilesList.map((f) => {
        const value = this.isUriMode ? f.uri : f.name
        const baseFilename = basename(value)
        return { ...f, name: baseFilename, value: value }
      })
      collectionFiles.sort((a, b) => alphaNumSort(a.value, b.value))
      return collectionFiles
    },
    dimensionsId() {
      return this.isRawDataCollection ? this.collection?.dimensionsId : null
    },
    dimensionsValues() {
      const dimNames = this.collection?.dimensionsValues || []
      return this.isRawDataCollection ? dimNames : dimNames.map(() => null)
    },
    dimensionsCount: {
      get() {
        return this.dimensionsValues?.length || 0
      },
      set(newCount) {
        if (newCount < MIN_DIMENSIONS) newCount = MIN_DIMENSIONS
        if (newCount >= MAX_DIMENSIONS) newCount = MAX_DIMENSIONS
        this.setDimensionValues(0, newCount)
      },
    },
    collectionDatasetsProperties() {
      return this.datasetPropertiesPerAssayTypeJson[this.assayTechnology] || {}
    },
    hasAssayTypeSet() {
      return this.assayType !== null
    },
    datasetsProperties() {
      let props = []
      let naProp = { id: 'na', label: 'Not Applicable' }
      if (this.isRawDataCollection) {
        let collProps = this.collectionDatasetsProperties[this.dimensionsId]
        collProps = collProps || []
        props = [
          ...collProps,
          ...(this.dimensionsCount > collProps.length ? [naProp] : []),
        ]
      } else {
        props = [naProp]
      }
      return props
    },
    processedDatasetsNames() {
      return [...this.datasetsNamesMatches].map((matches, i) => {
        let name = this.datasetsNames[i]?.value || ''
        if (matches.length > 1) {
          const m = [...matches].splice(1, matches.length)
          name = m.join('')
        }
        return name
      })
    },
    errors() {
      const errorNoName = {
        'no-name': 'Collection name cannot be empty',
      }
      return {
        ...((!this.collectionName && errorNoName) || {}),
      }
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0
    },
    hasSelectedAssay() {
      return this.isRawDataCollection && this.hasAssayTypeSet
    },
    canStageData() {
      return (
        !this.hasErrors && (!this.isRawDataCollection || this.hasSelectedAssay)
      )
    },
    advancedFiltersStatus() {
      return [
        (this.nameMask || '').length > 0,
        (this.datasetNameFiltInput || '').length > 0,
        (this.fileFilterInput || '').length > 0,
      ]
    },
    advancedFiltersNumber() {
      return this.advancedFiltersStatus.filter((s) => s === true).length
    },
    showHelp() {
      if (this.storeShowHelp === null)
        return (this.collection?.dimensionsValues || []).length > 1
      else return this.storeShowHelp
    },
  },
  methods: {
    pluralize,
    ...mapMutations('dataloader/main', { setShowHelp: 'SET_SHOW_HELP' }),
    ...mapActions('dataloader/collections', {
      edit: 'editCollection',
      changeIsRawData: 'setIsRaw',
      changeDimensionsId: 'setDimensionsId',
      spliceDimensions: 'spliceDimensions',
      changeIsUriMode: 'setIsUriMode',
      changeFileFilterInput: 'setFileFilterInput',
      changeFileFilterIsRegex: 'setFileFilterIsRegex',
    }),
    ...mapMutations('dataloader/collections', {
      setError: 'SET_ERROR',
      setDimensionValue: 'SET_DIMENSION_VALUE',
    }),
    setIsRawDataCollection(isRawData) {
      this.changeIsRawData({
        collectionId: this.selectedCollectionId,
        isRawData,
      })
      this.resetDimensions()
    },
    setIsUriMode(isUriMode) {
      this.changeIsUriMode({
        collectionId: this.selectedCollectionId,
        isUriMode,
      })
      // initialise a dataset name filter input to auto remove path
      if (isUriMode)
        this.initDatasetNameFiltInput = `file://${this.dropboxRoot}/(.*)`
      else this.initDatasetNameFiltInput = ''
    },
    resetDimensionsId() {
      this.changeDimensionsId({
        collectionId: this.selectedCollectionId,
        dimensionsId: Object.keys(this.collectionDatasetsProperties)[0] || null,
      })
    },
    resetDimensions() {
      this.resetDimensionsId()
      this.changeDimensionsId({
        collectionId: this.selectedCollectionId,
        dimensionsId: this.dimensionsId,
      })
      this.setDimensionValues(0, this.dimensionsCount)
    },
    setDimensionValues(start = 0, end = this.dimensionsCount) {
      const collectionId = this.selectedCollectionId
      const iRange = range(start, end)
      this.spliceDimensions({ collectionId, endIndex: end })
      // nullNames
      for (let index in iRange) {
        let props = this.isRawDataCollection
          ? this.datasetsProperties[index]
          : { id: null, prop_value: null }
        const dimensionValue = props?.prop_value
        this.setDimensionValue({ collectionId, index, dimensionValue })
      }
    },
    toggleHelp() {
      this.setShowHelp({ key: 'bldr-collections-table', value: !this.showHelp })
    },
  },
  watch: {
    errors: {
      handler: function (error, errorBefore) {
        const eKeys = Object.keys(error)
        const ebKeys = Object.keys(errorBefore || {})
        if (eKeys.length !== ebKeys.length) {
          this.setError({ id: this.selectedCollectionId, error: error })
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    if (
      this.isUriMode === true ||
      this.fileFilterInput !== '' ||
      this.nameMask !== ''
    ) {
      this.showAdvancedControls = true
    }
    // this.dimensionsCount = this.dimensionsValues?.length
    this.resetDimensions()
  },
}
</script>

<style lang="scss" scoped>
.collection-form {
  display: flex;
  flex-wrap: nowrap;
  position: relative;
  width: 100%;
  padding: 0.5rem;
  padding-right: 5rem;

  & > *:first-child {
    flex-grow: 1;
  }

  .field-label {
    text-align: left;
  }
  .field-body {
    flex-grow: 6;
  }

  .buttons.has-addons {
    position: relative;
    input {
      border-radius: 0;
      text-align: center;
      margin-right: -1px;
      width: 50px;
      z-index: 2;
    }
  }
}

.advanced-controls {
  position: absolute;
  top: 0;
  right: 0;
  background-color: var(--color-background-default);
  margin: 0 0.2rem;
  margin-bottom: 1rem;
  z-index: 2;

  ul li {
    &:not(:has(.buttons)) {
      flex-grow: 1;
      margin-right: 0.5rem;
      margin-bottom: 0.5rem;
    }
    margin-bottom: 0.5rem;
    .label {
      white-space: nowrap;
    }

    :deep(input.input) {
      min-width: 400px;
    }
  }

  ul > span > li {
    margin-top: 0;
  }

  .hoverable.icon {
    position: absolute;
    top: 0;
    right: 0;
    margin: 0.5rem;
    padding: 1rem;
  }

  .buttons:last-child {
    margin-bottom: unset;
  }
}
.collection-view {
  position: relative;
  .tags {
    position: absolute;
    top: 0;
    right: 0;
    transform: translateY(-100%);
  }
}
</style>
